import { ref, useIntervalFn, watchEffect } from '@/composition/vue/compositionApi'
import type { ValueOrRefOrGetter } from '@/types'

/**
 * Is used in the same way as the vue computed.
 * But it also updates the value every tickMs milliseconds.
 *
 * Usage example:
 * const msToNow = useComputedFromTime(() => {
 *   const date = someReactiveDate.value
 *   const now = new Date()
 *   return now.getTime() - date.getTime()
 * }, 1000)
 *
 * msToNow will be updated every second. Also it will be immediately updated when someReactiveDate changes.
 */
export const useComputedFromTime = <T>(callback: () => T, tickMs: ValueOrRefOrGetter<number> = 1000) => {
  const value = ref<T>()

  const update = () => {
    value.value = callback()
  }

  useIntervalFn(update, tickMs, { immediate: true })

  watchEffect(update)

  return value
}
